import { DataRow } from "@/api/types";
import FileDownloader from "@/components/FileDownloader/FileDownloader";
import Table, { Column } from "@/components/Table/Table";
import useTableState from "@/components/Table/hooks/useTableState";
import useTranslation from "@/hooks/useTranslation";
import {
  useCustomerImpactPerType,
  useCustomerImpactPerTypeExport,
} from "@/pages/dashboard/hooks/useCustomerImpact";
import { useDateRange } from "@/pages/dashboard/hooks/useDateRange";
import { formatAmountWithUnit, formatPercentage } from "@/pages/dashboard/utils/formatters";
import { DownloadOutlined } from "@ant-design/icons";
import { Segmented } from "antd";
import { useEffect, useState } from "react";
import Widget from "../../components/Widget/Widget";
import { ImpactMetric } from "../../utils/types";

export default function CustomerImpactPerType({ metric }: { metric: ImpactMetric }) {
  const { pagination, sort, search, filter, onSearchFilterSortPaginate, setPagination } =
    useTableState({
      defaultPagination: { page: 1, pageSize: 10 },
      defaultSort: {
        key: metric.key,
        order: "descend",
      },
    });
  const { startDate, endDate } = useDateRange();

  useEffect(() => {
    setPagination({ ...pagination, page: 1 });
  }, [startDate, endDate]);
  const translation = useTranslation();

  const [mode, setMode] = useState<"Absolute" | "Relative (%)">("Absolute");

  const columns: Column[] = [
    {
      label: "Name",
      key: "name",
      sortable: true,
      maxWidth: "2fr",
      searchable: true,
    },
    {
      label: "Type",
      key: "impact_type",
      maxWidth: "1fr",
      filterable: true,
      renderer: (record) => {
        return record.impact_type === "eol" ? "End of life" : "Usage";
      },
    },
    {
      label: "Total amount",
      key: "quantity",
      renderer: (record) => {
        return `${formatAmountWithUnit(record.quantity, record.unit, 0)}`;
      },
    },
    {
      label: metric.label,
      key: metric.key,
      sortable: true,
      renderer: (record) => {
        if (mode === "Absolute") {
          return metric.formatter(record[metric.key]);
        } else {
          return data
            ? formatPercentage(record[metric.key] / data?.company_customer_impact[metric.key])
            : NaN;
        }
      },
    },
  ];

  const { data, isLoading, isFetching } = useCustomerImpactPerType({
    pagination,
    filter,
    search,
    sort,
    metric: metric.key,
  });

  const exportMutation = useCustomerImpactPerTypeExport({
    pagination,
    filter,
    search,
    sort,
    metric: metric.key,
  });

  const tableData: DataRow[] =
    data?.customer_impact.map((row, index) => ({ ...row, key: index })) || [];

  return (
    <Widget
      title={"Customer impact side-by-side"}
      tooltip={translation["dashboard"]["customer"]["side_by_side"]}
      topRight={
        <>
          <Segmented
            options={["Absolute", "Relative (%)"]}
            onChange={setMode}
          />
          <FileDownloader
            fileUrlFn={() => exportMutation.mutateAsync()}
            buttonText={<DownloadOutlined />}
            fileOutputName="customer-impact-per-eol.xlsx"
            addTimestampAndCompany={true}
          />
        </>
      }
    >
      <Table
        availableFilters={{
          impact_type: [
            { label: "Usage", value: "electricity;consumable" },
            { label: "End of life", value: "eol" },
          ],
        }}
        hideRaster={true}
        type={"grey"}
        filter={filter}
        sort={sort}
        search={search}
        columns={columns}
        data={tableData}
        loading={isLoading || isFetching}
        onSearchFilterSortPaginate={onSearchFilterSortPaginate}
        pagination={pagination}
        totalRows={data?.total_rows}
        rowKey={"key"}
      />
    </Widget>
  );
}
